import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import COLORS from '../../../../styles/emotion/colors3';

import { Confetti } from '../../../../components/Confetti';
import Portal from '../../../../components/util/Portal';
import EnvelopeFront from './front.png';
import EnvelopeFlap from './flap.png';
import ZolaLogoEnvelopeFront from './frontZolaLogo.png';

import { ANIMATION_KEYS } from './constants';

import styles from './animatedCanvas.module.less';

interface AnimatedCanvasProps {
  animation: string;
  height: number;
  isGuestExperience: boolean;
  replayAnimation?: boolean;
  setReplayAnimation?: (replayAnimation: boolean) => void;
  setShowReplayCTA?: (showReplayCTA: boolean) => void;
  transformScale: string;
  width: number;
  // TODO: check with design if these two changes can be made universal
  useNewBrandColorConfetti?: boolean;
  zolaLogoOnEnvelopeFront?: boolean;
  delayAnimation?: boolean;
}

export const AnimatedCanvas: React.FC<AnimatedCanvasProps> = ({
  animation,
  children,
  height,
  isGuestExperience,
  replayAnimation,
  setReplayAnimation,
  setShowReplayCTA,
  transformScale,
  width,
  useNewBrandColorConfetti,
  zolaLogoOnEnvelopeFront,
  delayAnimation,
}) => {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedAnimation, setSelectedAnimation] = useState(animation);
  const [showClickToOpen, setShowClickToOpen] = useState(isGuestExperience);

  useEffect(() => {
    const delay = delayAnimation ? 1222 : 222;
    if (!isGuestExperience) {
      setOpen(false);
      setTimeout(() => {
        setOpen(true);
      }, delay);
    }
  }, [isGuestExperience, setOpen, delayAnimation]);

  useEffect(() => {
    if (animation !== selectedAnimation) {
      setOpen(false);
      setTimeout(() => {
        setSelectedAnimation(animation);
        setOpen(true);
      }, 222);
    }
  }, [animation, setOpen, selectedAnimation, setSelectedAnimation]);

  useEffect(() => {
    if (replayAnimation && setReplayAnimation) {
      setOpen(false);
      setTimeout(() => {
        setOpen(true);
        setReplayAnimation(false);
      }, 222);
    }
  }, [replayAnimation, setReplayAnimation, setOpen]);

  const handleOpenDSTD = () => {
    setOpen(true);
    setShowClickToOpen(false);
    if (setShowReplayCTA) setShowReplayCTA(true);
  };

  // make envelope slightly larger than card so box-shadow isn't cut off
  const envelopeWidth = width + 12;
  const envelopeHeight = envelopeWidth * 0.787;

  const inlineStyles = {
    height: envelopeHeight,
    width: envelopeWidth,
  };

  const element = document.getElementById('digitalCanvasBg');

  const cardTransition = delayAnimation
    ? `height .5s ease 1.7s, top 2s ease 1.7s`
    : `height .5s ease 0.7s, top 1s ease 0.7s`;
  return (
    <div
      className={cx(styles.animatedCanvas, {
        [styles.hover]: hover,
        [styles.open]: open,
        [styles.delayAnimation]: delayAnimation,
      })}
      style={{
        ...inlineStyles,
        transform: `translate(-50%, -50%) ${transformScale}`,
        top: '50%',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleOpenDSTD}
    >
      <div className={cx(styles.envelope, styles.back)} style={{ ...inlineStyles }} />
      <div className={cx(styles.envelope, styles.front)} style={{ ...inlineStyles }}>
        <img
          alt="envelope front"
          src={zolaLogoOnEnvelopeFront ? ZolaLogoEnvelopeFront : EnvelopeFront}
          width={envelopeWidth}
        />
      </div>
      <div className={styles.flapWrapper}>
        <div className={styles.flap}>
          <img alt="envelope flap" src={EnvelopeFlap} width={envelopeWidth} />
        </div>
      </div>
      <div
        className={styles.cardWrapper}
        style={{
          height: open ? height + 6 : envelopeHeight - 6,
          width: envelopeWidth,
          top: open ? '50%' : '',
          transform: open ? `translateY(-50%)` : '',
          transition: open ? cardTransition : '',
        }}
      >
        <div
          className={styles.cardElement}
          style={{
            height,
            width,
            transform: `translateX(-50%)`,
          }}
        >
          {children}
        </div>
      </div>
      {selectedAnimation === ANIMATION_KEYS.HEARTS && (
        <div className={styles.hearts} style={{ top: `${envelopeHeight / 2}px` }}>
          <div className={cx(styles.heart, styles.heart1)} />
          <div className={cx(styles.heart, styles.heart2)} />
          <div className={cx(styles.heart, styles.heart3)} />
        </div>
      )}
      {selectedAnimation === ANIMATION_KEYS.CONFETTI && open && element !== null && (
        <Portal portalContainer={element}>
          <Confetti
            duration={delayAnimation ? 15000 : undefined}
            confettiAmount={delayAnimation ? 300 : undefined}
            colors={
              useNewBrandColorConfetti
                ? [
                    COLORS.ROSE_100,
                    COLORS.LILAC_100,
                    COLORS.SKY_100,
                    COLORS.MARINE_100,
                    COLORS.MINT_100,
                    COLORS.WINE_100,
                    COLORS.FOREST_100,
                    COLORS.BLUSH_100,
                  ]
                : undefined
            }
          />
        </Portal>
      )}
      {showClickToOpen && <div className={styles.clickToOpen}>Click to open</div>}
    </div>
  );
};
